// src/pages/galerie/components/PhotoGallery.tsx
import React from "react";

type Folder = { name: string; count: number };
type ApiFolders = { ok: true; folders: Folder[] };
type ApiImages  = { ok: true; dir: string; images: string[] };
type ApiError   = { ok: false; error: string };

function PhotoGallery() {
  const [folders, setFolders] = React.useState<Folder[]>([]);
  const [current, setCurrent] = React.useState<string>("");
  const [images, setImages] = React.useState<string[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const isDev = import.meta.env.DEV;

  // 1) Charger la liste des dossiers
  React.useEffect(() => {
    (async () => {
      setError(null);
      try {
        const res = await fetch("/api/gallery.php");
        const json = (await res.json()) as ApiFolders | ApiError;
        if (!("ok" in json) || json.ok !== true) throw new Error((json as ApiError).error || "Erreur API");
        setFolders(json.folders);
        setCurrent(json.folders.at(-1)?.name || json.folders[0]?.name || "");
      } catch (e: any) {
        setError(isDev ? "Galerie indisponible en local." : (e?.message || "Erreur de chargement"));
        setFolders([]);
        setCurrent("");
      }
    })();
  }, [isDev]);

  // 2) Charger les images du dossier sélectionné
  React.useEffect(() => {
    if (!current) { setImages([]); return; }
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const u = new URL("/api/gallery.php", window.location.origin);
        u.searchParams.set("dir", current);
        const res = await fetch(u.toString());
        const json = (await res.json()) as ApiImages | ApiError;
        if (!("ok" in json) || json.ok !== true) throw new Error((json as ApiError).error || "Erreur API");
        setImages(json.images);
      } catch (e: any) {
        setError(isDev ? "Images indisponibles en local." : (e?.message || "Erreur de chargement"));
        setImages([]);
      } finally {
        setLoading(false);
      }
    })();
  }, [current, isDev]);

  return (
    <section className="py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900">Galerie</h2>

          {/* Sélecteur de dossier */}
          <div className="flex items-center gap-2">
            <label htmlFor="folder" className="text-sm text-gray-600">Dossier :</label>
            <select
              id="folder"
              className="border rounded-lg p-2 text-sm"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              disabled={!folders.length}
            >
              {folders.map((f) => (
                <option key={f.name} value={f.name}>
                  {f.name} {f.count ? `(${f.count})` : ""}
                </option>
              ))}
            </select>
          </div>
        </div>

        {error && <p className="text-red-600 text-sm mb-4">Erreur : {error}</p>}

        {loading ? (
          <p className="text-gray-600">Chargement…</p>
        ) : !images.length ? (
          <p className="text-gray-600">Aucune image à afficher.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
            {images.map((src, i) => (
              <figure
                key={src + i}
                className="rounded-xl overflow-hidden bg-gray-100 shadow-sm hover:shadow-md transition-shadow"
              >
                <img
                  src={src}
                  alt={`Photo ${i + 1} — ${current}`}
                  className="w-full aspect-[4/3] object-cover"
                  loading="lazy"
                />
              </figure>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

export default PhotoGallery;
export { PhotoGallery };
